import { useEffect } from "react";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import EditorialBand from "@/components/EditorialBand";
import Deconstruction from "@/components/Deconstruction";
import ElementQuadrants from "@/components/ElementQuadrants";
import Marquee from "@/components/Marquee";
import Showroom from "@/components/Showroom";
import Footer from "@/components/Footer";
import { useSEO } from "@/lib/useSEO";

const HERO_TEXTURES = [
  "/timber_boston_oak.jpg",
  "/carpet.jpg",
  "/tile_botticino.jpg",
  "/stone_taj_mahal.jpg",
];

// Search-action WebSite schema so Google can render a sitelinks searchbox.
const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://myfloorstudio.com.au/#website",
    url: "https://myfloorstudio.com.au/",
    name: "My Floor Studio",
    alternateName: "MFS · Drummoyne Flooring Studio",
    description:
      "Luxury flooring selection studio bringing Timber, Carpet, Tile and Stone under one roof for architects, designers and discerning homeowners.",
    publisher: { "@id": "https://myfloorstudio.com.au/#studio" },
    inLanguage: "en-AU",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate:
          "https://myfloorstudio.com.au/collection?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://myfloorstudio.com.au/#organization",
    name: "My Floor Studio",
    url: "https://myfloorstudio.com.au/",
    logo: {
      "@type": "ImageObject",
      url: "https://myfloorstudio.com.au/logo/logo-primary.png",
      width: 2000,
      height: 2000,
    },
    sameAs: ["https://www.instagram.com/myfloorstudio_au/"],
  },
];

export default function Landing() {
  useSEO({
    title:
      "Luxury Flooring Sydney · Timber, Carpet, Tile, Stone — My Floor Studio, Drummoyne",
    description:
      "Sydney's boutique luxury flooring studio in Drummoyne. Timber, Carpet, Tile and Stone — curated under one roof for architects, designers and discerning homeowners. Book a private consultation.",
    path: "/",
    keywords:
      "luxury flooring Sydney, timber flooring, wide plank oak, luxury carpet, natural stone flooring, porcelain tile, terrazzo, herringbone parquet, chevron flooring, Drummoyne flooring showroom, architect trade portal, interior designer samples, engineered oak flooring, French oak flooring, marble tile Sydney, Australian premium flooring",
    imageAlt: "My Floor Studio · luxury flooring studio in Drummoyne, Sydney",
    jsonLd: HOME_JSONLD,
  });
  // Preload the four hero puzzle textures ONLY on the landing page so other
  // routes don't emit "preloaded resource not used" console warnings.
  useEffect(() => {
    const links = HERO_TEXTURES.map((href) => {
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.href = href;
      l.fetchPriority = "high";
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l.parentNode?.removeChild(l));
  }, []);
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <Manifesto />
        <EditorialBand
          image="https://images.unsplash.com/photo-1773754532196-014342510e64?fm=jpg&q=85&w=2000&auto=format&fit=crop"
          kicker="Considered Living"
          title={<>Where every surface<br />is chosen with intent</>}
          caption="Curated for architects, designers and discerning homeowners — materials that hold their beauty for decades."
        />
        <Deconstruction />
        <ElementQuadrants />
        <Marquee />
        <EditorialBand
          image="https://images.unsplash.com/photo-1758448511255-ac2a24a135d7?fm=jpg&q=85&w=2000&auto=format&fit=crop"
          kicker="The Showroom"
          title={<>A tactile, one-stop<br />selection studio</>}
          caption="Experience full-scale timber, carpet, tile and stone under considered studio lighting at 184 Victoria Road, Drummoyne."
        />
        <Showroom />
      </main>
      <Footer />
    </>
  );
}
